import { message, noticeOf, rendererNotice } from "./messages"
import { type VariantProps, cva } from "class-variance-authority"
import { Slot } from "radix-ui"
import type { ComponentProps } from "react"
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { exactText, stringifyExact } from "./json"
import { WidgetHostError, useRuntime } from "./runtime"

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-[background-color,color,border-color,box-shadow] duration-150 ease-out disabled:pointer-events-none disabled:opacity-45 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/88 active:bg-primary/80",
        secondary: "border border-border bg-raised text-foreground hover:border-input hover:bg-muted",
        ghost: "text-muted-foreground hover:bg-raised hover:text-foreground",
        destructive: "border border-destructive/40 bg-destructive-soft text-destructive hover:bg-destructive/18",
        link: "h-auto px-0 text-primary underline-offset-4 hover:underline",
      },
      size: { sm: "h-7 px-2.5 text-xs", md: "h-8 px-3 text-sm", icon: "size-7" },
    },
    defaultVariants: { variant: "secondary", size: "md" },
  },
)

export function Button({ className, variant, size, asChild = false, ...props }: ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "button"
  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size }), className)} {...props} />
}

export function Input({ className, type = "text", ...props }: ComponentProps<"input">) {
  return <input type={type} data-slot="input" className={cn("h-8 w-full min-w-0 rounded-md border border-input bg-background px-2.5 text-sm text-foreground transition-[border-color,box-shadow] duration-150 outline-none", "placeholder:text-subtle-foreground focus-visible:border-primary/60 focus-visible:ring-3 focus-visible:ring-ring/40 focus-visible:outline-none", "aria-invalid:border-destructive/70 aria-invalid:ring-destructive/25 disabled:cursor-not-allowed disabled:opacity-50", className)} {...props} />
}

const badgeVariants = cva("inline-flex h-5 shrink-0 items-center gap-1 rounded-sm px-1.5 text-2xs font-medium whitespace-nowrap [&_svg]:size-3", {
  variants: {
    tone: {
      neutral: "bg-raised text-muted-foreground",
      outline: "border border-border text-muted-foreground",
      primary: "bg-primary-soft text-primary",
      success: "bg-success-soft text-success",
      warning: "bg-warning-soft text-warning",
      danger: "bg-destructive-soft text-destructive",
    },
  },
  defaultVariants: { tone: "neutral" },
})

export function Badge({ className, tone, ...props }: ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return <span data-slot="badge" className={cn(badgeVariants({ tone }), className)} {...props} />
}

export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return <div data-slot="skeleton" aria-hidden className={cn("animate-pulse rounded-sm bg-raised", className)} {...props} />
}

/**
 * One data value. Exact values (numbers, booleans, null, JSON) read left to right inside an isolated
 * run, so an Arabic page cannot reorder `-12` into `12-`; text takes the direction of its own content.
 */
export function Value({ value, className }: { value: unknown; className?: string }) {
  if (value === null || value === undefined) return <span dir="ltr" className={cn("text-subtle-foreground", className)}>null</span>
  if (typeof value === "boolean") return <span dir="ltr" className={cn("font-mono text-xs text-primary", className)}>{String(value)}</span>
  if (typeof value === "number") return <span dir="ltr" className={cn("font-mono text-xs tabular", className)}>{String(value)}</span>
  const exact = exactText(value)
  if (exact !== undefined) return <span dir="ltr" className={cn("font-mono text-xs tabular", className)}>{exact}</span>
  if (typeof value === "string") return <span dir="auto" className={cn("break-words", className)}>{value}</span>
  return <code dir="ltr" className={cn("font-mono text-2xs break-all text-muted-foreground", className)}>{stringifyExact(value)}</code>
}

export function ErrorState({ error, what, inline = false }: { error: unknown; what: string; inline?: boolean }) {
  const { locale, host } = useRuntime()
  const refusal = error instanceof WidgetHostError ? error.refusal : undefined
  const className = inline ? "mx-0 px-0 py-4" : "px-4 py-6"
  const title = refusal
    ? refusal.category === "permission" || refusal.category === "visibility" ? message(locale, "error.cannotRead", { what }) : refusal.message
    : message(locale, "error.cannotLoad", { what })
  return (
    <div role="alert" className={cn("grid gap-1 rounded-md border border-border-subtle text-sm", className, refusal?.category === "permission" || refusal?.category === "visibility" ? "text-warning" : "text-destructive")}>
      <strong>{title}</strong>
      <span>{refusal?.explanation ?? host.describeError?.(error) ?? rendererNotice(noticeOf(error), locale)}</span>
      {refusal ? <span className="text-xs"><code>{refusal.code}</code> · {refusal.category}</span> : null}
    </div>
  )
}
